import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import { CommonLoading } from '../../../../Utils/commonLoading';
import { CommonPaginationModel } from '../../../../Utils/commonPagination';
import toaster from '../../../../Utils/toaster';
import { buildCertificateRenderModel } from './skills-academy-certificate/certificateRender';
import { CertificateRenderModel } from './skills-academy-certificate/certificateTypes';
import { CertificatePdfExporter } from './skills-academy-certificate/CertificatePdfExporter';
import { CertificateViewer } from './skills-academy-certificate/CertificateViewer';
import { AasaCertificateImage, CERTIFICATE_ASSET_BASE_URL } from './skills-academy-certificate/AasaCertificateImage';
import { fetchCertificate } from './skills-academy-certificate/certificateApi';
import {
  certificateLinesFromSnapshot,
  downloadBlob,
  isCertificateDesign,
  renderCertificatePdf,
  type CertificateLines,
} from './skills-academy-certificate/certificate-render';

type Props = {
  linguistId: string;
};

type Course = {
  id: number;
  courseName: string;
  description: string;
  assignmentType: string;
  assignedDate: string;
  dueDate: string | null;
  status: string;
  result: string | null;
  certificateVerificationCode: string;
};

type DrawnCertificate = {
  lines: CertificateLines;
  templateKey: string;
  certificateNumber: string;
  title: string;
  learnerName: string;
};

type LoadedCertificate =
  | { kind: 'drawn'; certificate: DrawnCertificate }
  | { kind: 'legacy'; model: CertificateRenderModel }
  | { kind: 'revoked' };

const RESULT_COLORS: Record<string, { background: string; color: string }> = {
  Pass: { background: '#DCFCE7', color: '#166534' },
  'Pending review': { background: '#FEF3C7', color: '#92400E' },
  Fail: { background: '#FEE2E2', color: '#991B1B' },
};

export const AdAstraSkillsAcademyView: React.FC<Props> = ({ linguistId }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [summary, setSummary] = useState({ totalAssigned: 0, completePassed: 0, pending: 0, expired: 0, failed: 0, pendingReview: 0 });
  const [isLoading, setIsLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const certificateCacheRef = useRef(new Map<string, LoadedCertificate>());
  const [pendingCertificate, setPendingCertificate] = useState<{ code: string; action: 'view' | 'download' } | null>(null);
  const [viewingCertificate, setViewingCertificate] = useState<CertificateRenderModel | null>(null);
  const [viewingDrawnCertificate, setViewingDrawnCertificate] = useState<DrawnCertificate | null>(null);
  const [exportingCertificate, setExportingCertificate] = useState<CertificateRenderModel | null>(null);

  const loadCertificate = async (verificationCode: string): Promise<LoadedCertificate> => {
    const cached = certificateCacheRef.current.get(verificationCode);
    if (cached) return cached;
    const certificate = await fetchCertificate(verificationCode);
    if (certificate.isRevoked) return { kind: 'revoked' };

    const lines = isCertificateDesign(certificate.templateKey)
      ? certificateLinesFromSnapshot(certificate.certificateSnapshotJson)
      : null;
    const loaded: LoadedCertificate = lines
      ? {
          kind: 'drawn',
          certificate: {
            lines,
            templateKey: certificate.templateKey,
            certificateNumber: certificate.certificateNumber,
            title: certificate.courseName || 'Certificate',
            learnerName: lines.learnerName || '',
          },
        }
      : { kind: 'legacy', model: buildCertificateRenderModel(certificate) };
    certificateCacheRef.current.set(verificationCode, loaded);
    return loaded;
  };

  const reportRevoked = () => toaster('error', 'This certificate has been revoked.');

  const handleViewCertificate = async (verificationCode: string) => {
    setPendingCertificate({ code: verificationCode, action: 'view' });
    try {
      const loaded = await loadCertificate(verificationCode);
      if (loaded.kind === 'drawn') setViewingDrawnCertificate(loaded.certificate);
      else if (loaded.kind === 'legacy') setViewingCertificate(loaded.model);
      else reportRevoked();
    } catch {
      toaster('error', 'Error loading certificate.');
    } finally {
      setPendingCertificate(null);
    }
  };

  const handleDownloadCertificate = async (verificationCode: string) => {
    setPendingCertificate({ code: verificationCode, action: 'download' });
    try {
      const loaded = await loadCertificate(verificationCode);
      if (loaded.kind === 'legacy') {
        setExportingCertificate(loaded.model);
        return;
      }
      if (loaded.kind === 'drawn') {
        const { lines, templateKey, certificateNumber, learnerName } = loaded.certificate;
        const pdf = await renderCertificatePdf({
          lines,
          templateKey,
          assetBaseUrl: CERTIFICATE_ASSET_BASE_URL,
          title: `Certificate of Completion: ${learnerName}`,
        });
        downloadBlob(pdf, `${certificateNumber}-letter.pdf`);
      } else {
        reportRevoked();
      }
    } catch {
      toaster('error', 'Error downloading certificate.');
    }
    setPendingCertificate(null);
  };

  const handleCertificateExported = (succeeded: boolean) => {
    if (!succeeded) toaster('error', 'Error downloading certificate.');
    setExportingCertificate(null);
    setPendingCertificate(null);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      if (!linguistId) return;
      setIsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/learning-studio/users/${linguistId}/courses`, {
          params: { page: currentPage, pageSize: rowsPerPage }
        });
        
        const data = response.data;
        setSummary({
          totalAssigned: data.totalAssigned || 0,
          completePassed: data.completePassed || 0,
          pending: data.pending || 0,
          expired: data.expired || 0,
          failed: data.failed || 0,
          pendingReview: data.pendingReview || 0
        });

        const fetchedCourses = (data.courses?.items || []).map((inv: any) => {
          let mappedStatus = 'Pending';
          if (inv.status === 'Completed' || inv.status === 'Submitted' || inv.submittedAtUtc) {
            mappedStatus = 'Complete';
          } else if (inv.status === 'Expired') {
            mappedStatus = 'Expired';
          }

          let result: string | null = null;
          if (inv.templateType !== 'Training') {
            if (inv.isPendingReview) result = 'Pending review';
            else if (inv.passed === true) result = 'Pass';
            else if (inv.passed === false) result = 'Fail';
          }

          return {
            id: inv.id,
            courseName: inv.templateTitle,
            description: inv.notes || '',
            assignmentType: inv.templateType,
            assignedDate: inv.createdAtUtc,
            dueDate: inv.dueAtUtc,
            status: mappedStatus,
            result,
            certificateVerificationCode: inv.certificateVerificationCode || ''
          };
        });
        
        setCourses(fetchedCourses);
        setTotalRecords(data.courses?.totalItems || 0);
        setTotalPages(data.courses?.totalPages || 1);
      } catch (error) {
        console.error("Failed to fetch courses:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, [linguistId, currentPage, rowsPerPage]);

  const { totalAssigned, completePassed, pending, expired, failed, pendingReview } = summary;

  const formatDate = (isoString: string | null) => {
    if (!isoString) return '—';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}/${day}/${d.getFullYear()}`;
  };

  const isOverdue = (dueDateStr: string | null, status: string) => {
    if (!dueDateStr || status === 'Complete') return false;
    const due = new Date(dueDateStr);
    return due < new Date();
  };

  return (
    <>
      {isLoading && <CommonLoading />}
      <div className="font-['Poppins']">
        <div className="row g-5 mb-10">
          
          <div className="col">
            <div className="bg-white rounded-xl p-6 border border-[#E2E8F0] shadow-sm h-100" style={{ borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #E2E8F0' }}>
              <div className="text-[12px] fw-bold text-[#34373b] mb-2" style={{ fontSize: '12px', fontWeight: 'bold', color: '#34373b', marginBottom: '0.5rem' }}>TOTAL ASSIGNED</div>
              <div className="text-[32px] fw-bold text-black" style={{ fontSize: '32px', fontWeight: 'bold', color: 'black' }}>{totalAssigned}</div>
            </div>
          </div>
          
          <div className="col">
            <div className="rounded-xl p-6 border border-[#E2E8F0] shadow-sm h-100" style={{ backgroundColor: 'rgba(29, 215, 91, 0.1)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #E2E8F0' }}>
              <div className="text-[12px] fw-bold text-[#34373b] mb-2" style={{ fontSize: '12px', fontWeight: 'bold', color: '#34373b', marginBottom: '0.5rem' }}>COMPLETE/PASSED</div>
              <div className="text-[32px] fw-bold" style={{ fontSize: '32px', fontWeight: 'bold', color: '#15803D' }}>{completePassed}</div>
            </div>
          </div>
          
          <div className="col">
            <div className="rounded-xl p-6 border border-[#E2E8F0] shadow-sm h-100" style={{ backgroundColor: 'rgba(246, 197, 198, 0.3)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #E2E8F0' }}>
              <div className="text-[12px] fw-bold text-[#34373b] mb-2" style={{ fontSize: '12px', fontWeight: 'bold', color: '#34373b', marginBottom: '0.5rem' }}>PENDING</div>
              <div className="text-[32px] fw-bold" style={{ fontSize: '32px', fontWeight: 'bold', color: '#C2410C' }}>{pending}</div>
            </div>
          </div>

          <div className="col">
            <div className="rounded-xl p-6 border border-[#E2E8F0] shadow-sm h-100" style={{ backgroundColor: 'rgba(226, 232, 240, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #E2E8F0' }}>
              <div className="text-[12px] fw-bold text-[#34373b] mb-2" style={{ fontSize: '12px', fontWeight: 'bold', color: '#34373b', marginBottom: '0.5rem' }}>EXPIRED</div>
              <div className="text-[32px] fw-bold" style={{ fontSize: '32px', fontWeight: 'bold', color: '#475569' }}>{expired}</div>
            </div>
          </div>
          
          <div className="col">
            <div className="rounded-xl p-6 border border-[#E2E8F0] shadow-sm h-100" style={{ backgroundColor: 'rgba(254, 243, 199, 0.6)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #E2E8F0' }}>
              <div className="text-[12px] fw-bold text-[#34373b] mb-2" style={{ fontSize: '12px', fontWeight: 'bold', color: '#34373b', marginBottom: '0.5rem' }}>PENDING REVIEW</div>
              <div className="text-[32px] fw-bold" style={{ fontSize: '32px', fontWeight: 'bold', color: '#92400E' }}>{pendingReview}</div>
            </div>
          </div>

          <div className="col">
            <div className="rounded-xl p-6 border border-[#E2E8F0] shadow-sm h-100" style={{ backgroundColor: 'rgba(215, 56, 60, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #E2E8F0' }}>
              <div className="text-[12px] fw-bold text-[#34373b] mb-2" style={{ fontSize: '12px', fontWeight: 'bold', color: '#34373b', marginBottom: '0.5rem' }}>FAILED</div>
              <div className="text-[32px] fw-bold" style={{ fontSize: '32px', fontWeight: 'bold', color: '#B91C1C' }}>{failed}</div>
            </div>
          </div>

        </div>

        <div className="border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm" style={{ border: '1px solid #E2E8F0', borderRadius: '0.75rem', overflow: 'hidden' }}>
          <div className="overflow-x-auto" style={{ overflowX: 'auto' }}>
            <table className="w-100 text-left border-collapse" style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
              <thead className="bg-[#F8FAFC]" style={{ backgroundColor: '#F8FAFC' }}>
                <tr>
                  <th className="px-6 py-4 text-[11px] fw-bold text-[#94A3B8] text-uppercase" style={{ padding: '1rem 1.5rem', fontSize: '11px', fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Course Name & Description</th>
                  <th className="px-6 py-4 text-[11px] fw-bold text-[#94A3B8] text-uppercase text-center" style={{ padding: '1rem 1.5rem', fontSize: '11px', fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.05em' }}>Assignment Type</th>
                  <th className="px-6 py-4 text-[11px] fw-bold text-[#94A3B8] text-uppercase text-center" style={{ padding: '1rem 1.5rem', fontSize: '11px', fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.05em' }}>Assigned Date</th>
                  <th className="px-6 py-4 text-[11px] fw-bold text-[#94A3B8] text-uppercase text-center" style={{ padding: '1rem 1.5rem', fontSize: '11px', fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.05em' }}>Due Date</th>
                  <th className="px-6 py-4 text-[11px] fw-bold text-[#94A3B8] text-uppercase text-center" style={{ padding: '1rem 1.5rem', fontSize: '11px', fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.05em' }}>Status</th>
                  <th className="px-6 py-4 text-[11px] fw-bold text-[#94A3B8] text-uppercase text-center" style={{ padding: '1rem 1.5rem', fontSize: '11px', fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.05em' }}>Result</th>
                  <th className="px-6 py-4 text-[11px] fw-bold text-[#94A3B8] text-uppercase text-center" style={{ padding: '1rem 1.5rem', fontSize: '11px', fontWeight: 'bold', color: '#94A3B8', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.05em' }}>Certificate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]" style={{ borderTop: '1px solid #F1F5F9' }}>
                {courses.length === 0 ? (
                    <tr>
                        <td colSpan={7} className="text-center py-6 text-muted" style={{ padding: '1.5rem', textAlign: 'center' }}>No courses assigned.</td>
                    </tr>
                ) : (
                    courses.map((course, idx) => (
                    <tr key={idx} className="transition-colors hover:bg-gray-50" style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td className="px-6 py-6" style={{ padding: '1.5rem', maxWidth: '28rem' }}>
                        <div className="text-[15px] fw-bold text-[#0F172A] mb-1" style={{ fontSize: '15px', fontWeight: 'bold', color: '#0F172A', marginBottom: '0.25rem' }}>{course.courseName}</div>
                        <div className="text-[13px] text-[#64748B]" style={{ fontSize: '13px', color: '#64748B', lineHeight: '1.625' }}>{course.description}</div>
                        </td>
                        <td className="px-6 py-6 text-center" style={{ padding: '1.5rem', textAlign: 'center' }}>
                        <span className={`px-3 py-1 rounded-pill text-[11px] fw-bold`} style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', backgroundColor: course.assignmentType === 'Training' ? '#E0F2FE' : '#F3E8FF', color: course.assignmentType === 'Training' ? '#0369A1' : '#7E22CE' }}>
                            {course.assignmentType}
                        </span>
                        </td>
                        <td className="px-6 py-6 text-center text-[13px] text-[#475569]" style={{ padding: '1.5rem', textAlign: 'center', fontSize: '13px', color: '#475569' }}>
                        {formatDate(course.assignedDate)}
                        </td>
                        <td className="px-6 py-6 text-center" style={{ padding: '1.5rem', textAlign: 'center' }}>
                        <div className={`d-flex align-items-center justify-content-center gap-1 text-[13px] fw-semibold ${isOverdue(course.dueDate, course.status) ? 'text-danger' : 'text-[#475569]'}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem', fontSize: '13px', fontWeight: '500', color: isOverdue(course.dueDate, course.status) ? '#DC2626' : '#475569' }}>
                            {isOverdue(course.dueDate, course.status) && <i className="bi bi-exclamation-triangle-fill"></i>}
                            {formatDate(course.dueDate)}
                        </div>
                        </td>
                        <td className="px-6 py-6 text-center" style={{ padding: '1.5rem', textAlign: 'center' }}>
                        <div className={`d-inline-flex align-items-center gap-2 px-3 py-1 rounded text-[13px] fw-semibold`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.75rem', borderRadius: '0.375rem', fontSize: '13px', fontWeight: '500', backgroundColor: course.status === 'Complete' ? '#F0FDF4' : (course.status === 'Expired' ? '#F1F5F9' : '#FFFBEB'), color: course.status === 'Complete' ? '#166534' : (course.status === 'Expired' ? '#475569' : '#92400E'), border: `1px solid ${course.status === 'Complete' ? '#DCFCE7' : (course.status === 'Expired' ? '#E2E8F0' : '#FEF3C7')}` }}>
                            <i className={course.status === 'Complete' ? "bi bi-check" : (course.status === 'Expired' ? "bi bi-x-circle" : "bi bi-hourglass")}></i>
                            {course.status}
                        </div>
                        </td>
                        <td className="px-6 py-6 text-center" style={{ padding: '1.5rem', textAlign: 'center' }}>
                        {!course.result || course.result === '—' ? (
                            <span className="text-[#CBD5E1]" style={{ color: '#CBD5E1' }}>—</span>
                        ) : (
                            <span className={`px-3 py-1 rounded-pill text-[11px] fw-bold`} style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold', backgroundColor: RESULT_COLORS[course.result]?.background ?? '#FEE2E2', color: RESULT_COLORS[course.result]?.color ?? '#991B1B' }}>
                            {course.result}
                            </span>
                        )}
                        </td>
                        <td className="px-6 py-6 text-center" style={{ padding: '1.5rem', textAlign: 'center' }}>
                        {course.certificateVerificationCode ? (
                            <div className="d-inline-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-icon btn-bg-light btn-color-gray-700 btn-active-color-primary btn-sm rounded-circle"
                                title="View certificate"
                                aria-label="View certificate"
                                disabled={pendingCertificate !== null}
                                onClick={() => handleViewCertificate(course.certificateVerificationCode)}
                            >
                                {pendingCertificate?.code === course.certificateVerificationCode && pendingCertificate.action === 'view' ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                <i className="bi bi-eye fs-4"></i>
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-icon btn-sm"
                                title="Download certificate"
                                aria-label="Download certificate"
                                disabled={pendingCertificate !== null}
                                onClick={() => handleDownloadCertificate(course.certificateVerificationCode)}
                            >
                                {pendingCertificate?.code === course.certificateVerificationCode && pendingCertificate.action === 'download' ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                <KTSVG path="/media/icons/custom/certificate-download.svg" className="svg-icon-2x text-dark" />
                                )}
                            </button>
                            </div>
                        ) : (
                            <span className="text-[#CBD5E1]" style={{ color: '#CBD5E1' }}>—</span>
                        )}
                        </td>
                    </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {courses.length > 0 && (
          <CommonPaginationModel
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            total={totalRecords}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={(newRowsPerPage: number) => {
              setRowsPerPage(newRowsPerPage);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {(viewingCertificate || viewingDrawnCertificate) && (
        <Modal
          show
          onHide={() => {
            setViewingCertificate(null);
            setViewingDrawnCertificate(null);
          }}
          size="lg"
          centered
        >
          <Modal.Header className="py-3">
            <Modal.Title className="fw-bold">Certificate</Modal.Title>
            <button
              type="button"
              className="btn btn-icon btn-sm btn-active-light-primary ms-2"
              onClick={() => {
                setViewingCertificate(null);
                setViewingDrawnCertificate(null);
              }}
              aria-label="Close"
            >
              <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-2" />
            </button>
          </Modal.Header>
          <Modal.Body>
            {viewingDrawnCertificate ? (
              <AasaCertificateImage
                lines={viewingDrawnCertificate.lines}
                templateKey={viewingDrawnCertificate.templateKey}
                format="letter"
                alt={viewingDrawnCertificate.title}
              />
            ) : (
              viewingCertificate && <CertificateViewer model={viewingCertificate} />
            )}
          </Modal.Body>
        </Modal>
      )}

      {exportingCertificate && (
        <CertificatePdfExporter model={exportingCertificate} onFinished={handleCertificateExported} />
      )}
    </>
  );
};

export default AdAstraSkillsAcademyView;
